import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography } from '@mui/material';
import { SCUIButton } from '../../../../atomic/atoms';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CronUI from './Cron';
import { describeCronExpression } from '../../helper/ScheduleService.helper';
import { CROND } from './cronDialog.classes';

interface CronWrapperProps {
    cronExpression: string;
    onSave: (cronExpression: string) => void;
}

const CronWrapper: React.FC<CronWrapperProps> = ({ cronExpression, onSave }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentCronExpression, setCurrentCronExpression] = useState('');

    const { t } = useTranslation();

    const cronDescription = useMemo(() => {
        if (currentCronExpression) {
            return describeCronExpression(currentCronExpression, t);
        }
        return '';
    }, [currentCronExpression, t]);

    const handleDialogOpen = () => {
        setIsDialogOpen(true);
    };

    const handleDialogClose = (param?: any) => {
        if (!param?.isSave) {
            setCurrentCronExpression(cronExpression);
        }
        setIsDialogOpen(false);
    };

    const handleCronChange = (newCron: string) => {
        setCurrentCronExpression(newCron);
    };

    const handleSave = () => {
        onSave(currentCronExpression);
        handleDialogClose({ isSave: true });
    };

    useEffect(() => {
        if (cronExpression) {
            setCurrentCronExpression(cronExpression);
        }
    }, [cronExpression]);

    return (
        <>
            <Box className={CROND.root} sx={{ width: '100%' }}>
                <Box
                    className={CROND.previewRow}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#f7f7f7',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '0px 0px 0px 16px',
                        height: '36px',
                        width: '100%',
                    }}
                >
                    <Typography className={CROND.previewText} sx={{ height: '12px' }}>
                        {currentCronExpression || '* * * * *'}
                    </Typography>
                    <IconButton className={CROND.openButton} sx={{ marginLeft: '16px', borderRadius: '0px' }} onClick={handleDialogOpen}>
                        <EditIcon className={CROND.openIcon} />
                    </IconButton>
                </Box>
                {cronDescription && (
                    <Typography
                        className={CROND.descriptionText}
                        sx={{
                            marginTop: '8px',
                            fontSize: '12px',
                            color: '#666',
                            fontStyle: 'italic',
                        }}
                    >
                        {cronDescription}
                    </Typography>
                )}
            </Box>
            <Dialog
                className={CROND.dialog}
                open={isDialogOpen}
                onClose={() => handleDialogClose()}
                fullWidth
                maxWidth="sm"
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: '8px',
                        },
                    },
                }}
            >
                <DialogTitle
                    className={CROND.dialogTitle}
                    sx={{
                        paddingLeft: '36px !important',
                        fontWeight: '600px !important',
                        fontSize: '14px !important',
                        backgroundColor: '#f5f5f5',
                    }}
                >
                    {t('cron.cronDialog.configureCronJob')}
                    <IconButton
                        className={CROND.dialogCloseButton}
                        aria-label="close"
                        onClick={() => handleDialogClose()}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 6,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon className={CROND.dialogCloseIcon} />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={CROND.dialogContent}>
                    <CronUI onChange={handleCronChange} defaultCron={cronExpression} />
                    {cronDescription && (
                        <Box
                            className={CROND.previewBox}
                            sx={{ marginTop: '16px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}
                        >
                            <Typography className={CROND.previewBoxTitle} variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                {t('cron.description.preview')}
                            </Typography>
                            <Typography className={CROND.previewBoxText} variant="body2" sx={{ color: '#555' }}>
                                {cronDescription}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions className={CROND.actions}>
                    <Box sx={{ marginRight: '8px' }}>
                        <SCUIButton
                            className={CROND.cancelButton}
                            onClick={() => handleDialogClose()}
                            text={t('general.cancel')}
                            sizeType={'fixed'}
                            size={'s'}
                            type={'primary'}
                        />
                    </Box>
                    <Box>
                        <SCUIButton
                            className={CROND.saveButton}
                            onClick={handleSave}
                            text={t('general.save')}
                            sizeType={'fixed'}
                            size={'s'}
                            type={'primary'}
                        />
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CronWrapper;
